'use client';

import React, { useState, useMemo } from 'react';

import { Plus, Trash2, ShoppingCart, Search, CreditCard, Banknote, Smartphone, FileText, Clock, CheckCircle, Loader2, AlertCircle, X, Calculator, MapPin, MessageCircle } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';

import { toast } from 'sonner';

type PaymentMethod = 'Cash' | 'Deferred_Credit' | 'Bank_Card' | 'Cheque' | 'Mobile_Wallet';

interface OrderLineItem {
  product_id: string;
  product_name: string;
  unit_price: number;
  tax_rate: number;
  quantity: number;
  unit_type: string;
}

interface OrderFormData {
  supplier_id: string;
  payment_method: PaymentMethod;
  notes: string;
  items: OrderLineItem[];
}

const suppliers = [
{ id: 'sup-001', name: 'Grossiste Alaoui & Fils', zone: 'Centre_Ville', available_credit: 10180 },
{ id: 'sup-002', name: 'Distribution Halal Khwazat', zone: 'Khwazat', available_credit: 5850 },
{ id: 'sup-003', name: 'Maraîcher Bio Ville Haute', zone: 'Ville_Haute', available_credit: 3020 }];


const productsBySupplier: Record<string, {
  id: string;name: string;wholesale_price: number;tax_rate: number;
  unit_type: string;category: string;has_active_discount: boolean;discount_pct: number;
  image: string;alt: string;
}[]> = {
  'sup-001': [
  { id: 'prod-001', name: 'Fromage Frais Kiri 500g', wholesale_price: 28.50, tax_rate: 0.10, unit_type: 'Piece', category: 'Dairy', has_active_discount: true, discount_pct: 12, image: "https://img.rocket.new/generatedImages/rocket_gen_img_121f41113-1773795312148.png", alt: 'Fresh white cheese on wooden board' },
  { id: 'prod-004', name: 'Lait Entier Centrale 1L', wholesale_price: 7.80, tax_rate: 0.07, unit_type: 'Piece', category: 'Dairy', has_active_discount: false, discount_pct: 0, image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f67bca5b-1766529777771.png", alt: 'White milk carton on clean background' },
  { id: 'prod-007', name: 'Yaourt Nature Danone 4×125g', wholesale_price: 12.50, tax_rate: 0.07, unit_type: 'Carton_Box', category: 'Dairy', has_active_discount: false, discount_pct: 0, image: "https://img.rocket.new/generatedImages/rocket_gen_img_10b73677d-1768471097939.png", alt: 'White yogurt cups in packaging' },
  { id: 'prod-008', name: 'Beurre Paysan 250g', wholesale_price: 14.00, tax_rate: 0.07, unit_type: 'Piece', category: 'Dairy', has_active_discount: false, discount_pct: 0, image: "https://img.rocket.new/generatedImages/rocket_gen_img_156571d2c-1770258068384.png", alt: 'Butter block wrapped in paper on marble surface' },
  { id: 'prod-011', name: 'Pizza 4 Fromages Format Pro', wholesale_price: 22.00, tax_rate: 0.20, unit_type: 'Piece', category: 'Pizzas', has_active_discount: true, discount_pct: 5, image: "https://img.rocket.new/generatedImages/rocket_gen_img_1933c31e9-1772650700538.png", alt: 'Four cheese pizza with golden crust' }],

  'sup-002': [
  { id: 'prod-003', name: 'Poulet Entier Halal 1.2kg', wholesale_price: 32.00, tax_rate: 0.10, unit_type: 'Piece', category: 'Meats', has_active_discount: true, discount_pct: 8, image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f474d4d9-1773092220648.png", alt: 'Whole raw chicken on white background' },
  { id: 'prod-006', name: 'Viande Hachée Bœuf 500g', wholesale_price: 38.00, tax_rate: 0.10, unit_type: 'Piece', category: 'Meats', has_active_discount: false, discount_pct: 0, image: "https://images.unsplash.com/photo-1689760661349-0bc890d3ccee", alt: 'Raw ground beef in tray' },
  { id: 'prod-010', name: 'Escalope Dinde Halal 1kg', wholesale_price: 45.00, tax_rate: 0.10, unit_type: 'Kilogram', category: 'Meats', has_active_discount: true, discount_pct: 10, image: "https://images.unsplash.com/photo-1604503468506-a8da13d82791", alt: 'Raw turkey escalope slices on white background' }],

  'sup-003': [
  { id: 'prod-002', name: 'Tomates Fraîches Calibre A', wholesale_price: 4.20, tax_rate: 0.00, unit_type: 'Kilogram', category: 'Vegetables', has_active_discount: false, discount_pct: 0, image: "https://images.unsplash.com/photo-1693946565918-72325e27e4af", alt: 'Red ripe tomatoes in a crate' },
  { id: 'prod-005', name: 'Carottes Bio Botte 1kg', wholesale_price: 3.50, tax_rate: 0.00, unit_type: 'Kilogram', category: 'Vegetables', has_active_discount: true, discount_pct: 15, image: "https://images.unsplash.com/photo-1602496875335-b0fc93069cf9", alt: 'Fresh orange carrots with green tops on wood' },
  { id: 'prod-009', name: 'Oignons Rouges Sac 5kg', wholesale_price: 8.00, tax_rate: 0.00, unit_type: 'Carton_Box', category: 'Vegetables', has_active_discount: false, discount_pct: 0, image: "https://images.unsplash.com/photo-1680043026785-ddee499f03a5", alt: 'Red onions in a pile on wooden surface' },
  { id: 'prod-012', name: 'Courgettes Fraîches 1kg', wholesale_price: 5.20, tax_rate: 0.00, unit_type: 'Kilogram', category: 'Vegetables', has_active_discount: false, discount_pct: 0, image: "https://images.unsplash.com/photo-1596633608781-2824330afb10", alt: 'Fresh green zucchini courgettes on white background' }]

};

const paymentMethods: {value: PaymentMethod;label: string;icon: React.ElementType;description: string;}[] = [
{ value: 'Deferred_Credit', label: 'Crédit Différé', icon: Clock, description: 'Paiement sur compte crédit fournisseur' },
{ value: 'Cash', label: 'Espèces', icon: Banknote, description: 'Paiement immédiat à la livraison' },
{ value: 'Bank_Card', label: 'Carte Bancaire', icon: CreditCard, description: 'Paiement par TPE à la livraison' },
{ value: 'Cheque', label: 'Chèque', icon: FileText, description: 'Chèque à remettre au livreur' },
{ value: 'Mobile_Wallet', label: 'Mobile Wallet', icon: Smartphone, description: 'CIH Pay, OrangeMoney, etc.' }];


// ─── Calculator Widget ────────────────────────────────────────────────────────
function OrderCalculator({ onClose }: {onClose: () => void;}) {
  const [display, setDisplay] = React.useState('0');
  const [prev, setPrev] = React.useState<string | null>(null);
  const [op, setOp] = React.useState<string | null>(null);
  const [waitNext, setWaitNext] = React.useState(false);

  const handleDigit = (d: string) => {
    if (waitNext) {setDisplay(d);setWaitNext(false);return;}
    setDisplay(display === '0' ? d : display + d);
  };
  const handleDot = () => {
    if (waitNext) {setDisplay('0.');setWaitNext(false);return;}
    if (!display.includes('.')) setDisplay(display + '.');
  };
  const handleOp = (o: string) => {setPrev(display);setOp(o);setWaitNext(true);};
  const handleEquals = () => {
    if (!op || !prev) return;
    const a = parseFloat(prev),b = parseFloat(display);
    let result = 0;
    if (op === '+') result = a + b;else
    if (op === '-') result = a - b;else
    if (op === '×') result = a * b;else
    if (op === '÷') result = b !== 0 ? a / b : 0;
    setDisplay(parseFloat(result.toFixed(4)).toString());
    setPrev(null);setOp(null);setWaitNext(true);
  };
  const handleClear = () => {setDisplay('0');setPrev(null);setOp(null);setWaitNext(false);};
  const handleBackspace = () => {if (waitNext) return;setDisplay(display.length > 1 ? display.slice(0, -1) : '0');};
  const handlePercent = () => {setDisplay((parseFloat(display) / 100).toString());};

  const btn = (label: string, onClick: () => void, cls = '') =>
  <button key={label} onClick={onClick} className={`h-10 rounded-xl text-sm font-semibold transition-colors active:scale-95 ${cls}`}>{label}</button>;


  return (
    <div className="bg-card border border-border rounded-2xl shadow-xl overflow-hidden w-full">
      <div className="flex items-center justify-between px-3 py-2 bg-muted/50 border-b border-border">
        <div className="flex items-center gap-1.5">
          <Calculator size={13} className="text-primary" />
          <span className="text-xs font-semibold text-foreground">Calculatrice</span>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-0.5"><X size={14} /></button>
      </div>
      <div className="px-3 py-2 bg-gray-900 text-right">
        {op && prev && <p className="text-xs text-gray-400 font-mono truncate">{prev} {op}</p>}
        <p className="text-2xl font-bold text-white font-mono truncate">{display}</p>
      </div>
      <div className="p-2 grid grid-cols-4 gap-1.5">
        {btn('AC', handleClear, 'bg-red-100 text-red-700 hover:bg-red-200')}
        {btn('⌫', handleBackspace, 'bg-amber-100 text-amber-700 hover:bg-amber-200')}
        {btn('%', handlePercent, 'bg-muted text-muted-foreground hover:bg-muted/80')}
        {btn('÷', () => handleOp('÷'), `${op === '÷' ? 'bg-primary text-white' : 'bg-primary/10 text-primary hover:bg-primary/20'}`)}
        {btn('7', () => handleDigit('7'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('8', () => handleDigit('8'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('9', () => handleDigit('9'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('×', () => handleOp('×'), `${op === '×' ? 'bg-primary text-white' : 'bg-primary/10 text-primary hover:bg-primary/20'}`)}
        {btn('4', () => handleDigit('4'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('5', () => handleDigit('5'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('6', () => handleDigit('6'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('−', () => handleOp('-'), `${op === '-' ? 'bg-primary text-white' : 'bg-primary/10 text-primary hover:bg-primary/20'}`)}
        {btn('1', () => handleDigit('1'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('2', () => handleDigit('2'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('3', () => handleDigit('3'), 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('+', () => handleOp('+'), `${op === '+' ? 'bg-primary text-white' : 'bg-primary/10 text-primary hover:bg-primary/20'}`)}
        <button onClick={() => handleDigit('0')} className="col-span-2 h-10 rounded-xl text-sm font-semibold bg-background border border-border text-foreground hover:bg-muted transition-colors active:scale-95">0</button>
        {btn('.', handleDot, 'bg-background border border-border text-foreground hover:bg-muted')}
        {btn('=', handleEquals, 'bg-primary text-white hover:bg-primary/90')}
      </div>
    </div>);

}

export default function DirectOrderContent() {
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('Deferred_Credit');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedInvoiceNumber, setSubmittedInvoiceNumber] = useState('');
  const [showCalc, setShowCalc] = useState(false);

  const [lineItems, setLineItems] = useState<(OrderLineItem & {image: string;alt: string;has_active_discount: boolean;discount_pct: number;})[]>([]);

  const selectedSupplier = suppliers.find((s) => s.id === selectedSupplierId);
  const availableProducts = productsBySupplier[selectedSupplierId] || [];

  const filteredProducts = availableProducts.filter((p) =>
  p.name.toLowerCase().includes(productSearch.toLowerCase()) &&
  !lineItems.find((li) => li.product_id === p.id)
  );

  const addProduct = (product: typeof availableProducts[0]) => {
    const effectivePrice = product.has_active_discount ?
    product.wholesale_price * (1 - product.discount_pct / 100) :
    product.wholesale_price;
    setLineItems((prev) => [
    ...prev,
    {
      product_id: product.id,
      product_name: product.name,
      unit_price: effectivePrice,
      tax_rate: product.tax_rate,
      quantity: 1,
      unit_type: product.unit_type,
      image: product.image,
      alt: product.alt,
      has_active_discount: product.has_active_discount,
      discount_pct: product.discount_pct
    }]
    );
    setShowProductPicker(false);
    setProductSearch('');
  };

  const updateQuantity = (index: number, qty: number) => {
    if (qty < 1) return;
    setLineItems((prev) => prev.map((item, i) => i === index ? { ...item, quantity: qty } : item));
  };

  const removeItem = (index: number) => {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const orderTotals = useMemo(() => {
    const subtotalHT = lineItems.reduce((sum, item) => sum + item.unit_price * item.quantity, 0);
    const totalTax = lineItems.reduce((sum, item) => sum + item.unit_price * item.quantity * item.tax_rate, 0);
    const totalTTC = subtotalHT + totalTax;

    const taxBreakdown: Record<string, number> = {};
    lineItems.forEach((item) => {
      const key = `${(item.tax_rate * 100).toFixed(0)}%`;
      taxBreakdown[key] = (taxBreakdown[key] || 0) + item.unit_price * item.quantity * item.tax_rate;
    });

    return { subtotalHT, totalTax, totalTTC, taxBreakdown };
  }, [lineItems]);

  const creditWarning = selectedSupplier && paymentMethod === 'Deferred_Credit' &&
  orderTotals.totalTTC > selectedSupplier.available_credit;

  const handleSubmit = async () => {
    if (!selectedSupplierId) {toast.error('Veuillez sélectionner un fournisseur');return;}
    if (lineItems.length === 0) {toast.error('Ajoutez au moins un produit à la commande');return;}
    if (creditWarning) {toast.error('Dépassement du crédit disponible — choisissez un autre mode de paiement');return;}

    setLoading(true);
    await new Promise((r) => setTimeout(r, 1200));

    // Backend: INSERT INTO invoices_bons_livraison (retailer_id, supplier_id, payment_method, total_amount, fulfillment_status, items) VALUES (auth.uid(), selectedSupplierId, paymentMethod, totalTTC, 'Pending_Review', lineItems)
    const invoiceNumber = `BL-2026-0${842 + Math.floor(Math.random() * 10)}`;
    setSubmittedInvoiceNumber(invoiceNumber);
    setLoading(false);
    setSubmitted(true);
    toast.success(`Commande ${invoiceNumber} soumise avec succès`);

    // Auto-open WhatsApp with pre-filled order message
    const supplier = suppliers.find((s) => s.id === selectedSupplierId);
    const itemLines = lineItems.map((item) =>
    `• ${item.product_name} × ${item.quantity} = ${(item.unit_price * item.quantity).toFixed(2)} MAD`
    ).join('\n');
    const paymentLabel = paymentMethods.find((p) => p.value === paymentMethod)?.label || paymentMethod;
    const waMessage = [
    `🛒 *Nouvelle Commande — ${invoiceNumber}*`,
    ``,
    `📦 *Fournisseur:* ${supplier?.name}`,
    `📅 *Date:* ${new Date().toLocaleDateString('fr-MA')}`,
    ``,
    `*Articles:*`,
    itemLines,
    ``,
    `💰 *Sous-total HT:* ${orderTotals.subtotalHT.toFixed(2)} MAD`,
    `🧾 *TVA:* ${orderTotals.totalTax.toFixed(2)} MAD`,
    `✅ *Total TTC:* ${orderTotals.totalTTC.toFixed(2)} MAD`,
    ``,
    `💳 *Paiement:* ${paymentLabel}`,
    notes ? `📝 *Notes:* ${notes}` : '',
    ``,
    `Merci de confirmer la réception de cette commande.`].
    filter(Boolean).join('\n');

    // Use supplier's WhatsApp number — replace with actual number from DB
    const supplierWhatsApp = '212600000000'; // placeholder — replace with supplier.whatsapp_number from DB
    const waUrl = `https://wa.me/${supplierWhatsApp}?text=${encodeURIComponent(waMessage)}`;
    window.open(waUrl, '_blank');
  };

  const handleResendWhatsApp = () => {
    const supplier = suppliers.find((s) => s.id === selectedSupplierId);
    const itemLines = lineItems.map((item) =>
    `• ${item.product_name} × ${item.quantity} = ${(item.unit_price * item.quantity).toFixed(2)} MAD`
    ).join('\n');
    const paymentLabel = paymentMethods.find((p) => p.value === paymentMethod)?.label || paymentMethod;
    const waMessage = [
    `🛒 *Nouvelle Commande — ${submittedInvoiceNumber}*`,
    ``,
    `📦 *Fournisseur:* ${supplier?.name}`,
    `📅 *Date:* ${new Date().toLocaleDateString('fr-MA')}`,
    ``,
    `*Articles:*`,
    itemLines,
    ``,
    `💰 *Sous-total HT:* ${orderTotals.subtotalHT.toFixed(2)} MAD`,
    `🧾 *TVA:* ${orderTotals.totalTax.toFixed(2)} MAD`,
    `✅ *Total TTC:* ${orderTotals.totalTTC.toFixed(2)} MAD`,
    ``,
    `💳 *Paiement:* ${paymentLabel}`,
    notes ? `📝 *Notes:* ${notes}` : '',
    ``,
    `Merci de confirmer la réception de cette commande.`].
    filter(Boolean).join('\n');

    const supplierWhatsApp = '212600000000';
    const waUrl = `https://wa.me/${supplierWhatsApp}?text=${encodeURIComponent(waMessage)}`;
    window.open(waUrl, '_blank');
  };

  if (submitted) {
    const trackingUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/order-fulfillment-kanban-board?order=${submittedInvoiceNumber}`;
    const supplier = suppliers.find((s) => s.id === selectedSupplierId);
    const paymentLabel = paymentMethods.find((p) => p.value === paymentMethod)?.label;

    return (
      <div className="max-w-xl mx-auto py-10 fade-in space-y-5">
        {/* Success header */}
        <div className="flex flex-col items-center text-center pb-2">
          <div className="w-16 h-16 rounded-full bg-success-light flex items-center justify-center mb-4">
            <CheckCircle size={32} className="text-success" />
          </div>
          <h2 className="text-xl font-700 text-foreground mb-1">Commande Soumise !</h2>
          <p className="text-sm text-muted-foreground">
            Bon de livraison <span className="font-mono font-700 text-foreground">{submittedInvoiceNumber}</span> transmis au fournisseur.
          </p>
        </div>

        {/* Order recap card */}
        <div className="card-base space-y-4">
          <h3 className="text-sm font-700 text-foreground border-b border-border pb-3">📋 Récapitulatif de la Commande</h3>

          {/* Supplier + payment */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-muted/50 rounded-xl p-3">
              <p className="text-2xs text-muted-foreground font-600 mb-0.5">Fournisseur</p>
              <p className="text-sm font-700 text-foreground">{supplier?.name}</p>
              <p className="text-xs text-muted-foreground">{supplier?.zone.replace('_', ' ')}</p>
            </div>
            <div className="bg-muted/50 rounded-xl p-3">
              <p className="text-2xs text-muted-foreground font-600 mb-0.5">Mode de Paiement</p>
              <p className="text-sm font-700 text-primary">{paymentLabel}</p>
              <p className="text-xs text-muted-foreground">{new Date().toLocaleDateString('fr-MA')}</p>
            </div>
          </div>

          {/* Line items */}
          <div className="space-y-1.5">
            <p className="text-xs font-700 text-muted-foreground uppercase tracking-wide">Articles commandés</p>
            {lineItems.map((item, idx) =>
            <div key={idx} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                  <AppImage src={item.image} alt={item.alt} width={36} height={36} className="object-cover w-full h-full" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-600 text-foreground truncate">{item.product_name}</p>
                  <p className="text-2xs text-muted-foreground">{item.quantity} × {item.unit_price.toFixed(2)} MAD</p>
                </div>
                <span className="text-sm font-700 text-foreground font-tabular flex-shrink-0">
                  {(item.unit_price * item.quantity).toFixed(2)} MAD
                </span>
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="bg-muted/40 rounded-xl p-3 space-y-1.5">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Sous-total HT</span>
              <span className="font-tabular font-600">{orderTotals.subtotalHT.toFixed(2)} MAD</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>TVA</span>
              <span className="font-tabular font-600">+{orderTotals.totalTax.toFixed(2)} MAD</span>
            </div>
            <div className="flex justify-between text-sm font-800 text-foreground border-t border-border pt-1.5 mt-1">
              <span>Total TTC</span>
              <span className="font-tabular text-lg">{orderTotals.totalTTC.toFixed(2)} MAD</span>
            </div>
          </div>
        </div>

        {/* WhatsApp send status */}
        <div className="card-base">
          <h3 className="text-sm font-700 text-foreground mb-3">📲 Statut Envoi WhatsApp</h3>
          <div className="flex items-center gap-3 p-3 bg-[#25D366]/8 border border-[#25D366]/30 rounded-xl">
            <div className="w-9 h-9 rounded-full bg-[#25D366]/15 flex items-center justify-center flex-shrink-0">
              <MessageCircle size={18} className="text-[#128C7E]" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-700 text-[#128C7E]">Message envoyé automatiquement</p>
              <p className="text-xs text-muted-foreground">Détails de la commande transmis au fournisseur via WhatsApp</p>
            </div>
            <span className="flex-shrink-0 text-2xs font-700 px-2 py-1 bg-[#25D366]/15 text-[#128C7E] rounded-full border border-[#25D366]/30">
              ✓ Envoyé
            </span>
          </div>
          <button
            onClick={handleResendWhatsApp}
            className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#25D366] text-white rounded-xl text-sm font-700 hover:bg-[#1ebe5d] transition-colors">
            <MessageCircle size={14} />
            Renvoyer sur WhatsApp
          </button>
        </div>

        {/* Order tracking link */}
        <div className="card-base">
          <h3 className="text-sm font-700 text-foreground mb-3">🚚 Suivi de Livraison</h3>
          <p className="text-xs text-muted-foreground mb-3">
            Suivez l'avancement de votre commande <span className="font-mono font-700 text-foreground">{submittedInvoiceNumber}</span> en temps réel.
          </p>
          <div className="flex items-center gap-2 p-3 bg-primary-light border border-primary/20 rounded-xl mb-3">
            <MapPin size={14} className="text-primary flex-shrink-0" />
            <span className="text-xs text-primary font-600 truncate flex-1">{trackingUrl}</span>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(trackingUrl);
                toast.success('Lien copié !');
              }}
              className="text-2xs text-primary font-700 hover:underline flex-shrink-0 px-2 py-1 bg-primary/10 rounded-lg">
              
              Copier
            </button>
          </div>
          <a
            href="/order-fulfillment-kanban-board"
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-primary text-primary text-sm font-700 hover:bg-primary-light transition-colors">
            
            <MapPin size={14} />
            Ouvrir le Tableau de Suivi
          </a>
        </div>

        {/* Actions */}
        <button
          onClick={() => {setSubmitted(false);setLineItems([]);setSelectedSupplierId('');setNotes('');}}
          className="btn-primary w-full justify-center">
          <Plus size={15} />
          Nouvelle Commande
        </button>
      </div>);

  }

  return (
    <div className="space-y-5 fade-in">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h2 className="text-xl font-700 text-foreground">Bon de Commande Direct</h2>
          <p className="text-sm text-muted-foreground">Les commandes sont soumises en statut Pending_Review</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-6">
        {/* Left — order builder */}
        <div className="xl:col-span-2 space-y-5">
          {/* Step 1: Supplier selection */}
          <div className="card-base">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-800">1</div>
              <h3 className="text-sm font-700 text-foreground">Sélectionner le Fournisseur</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {suppliers.map((sup) =>
              <button
                key={sup.id}
                onClick={() => {setSelectedSupplierId(sup.id);setLineItems([]);}}
                className={`p-3 rounded-xl border text-left transition-all duration-150 ${
                selectedSupplierId === sup.id ?
                'border-primary bg-primary-light ring-2 ring-primary/20' : 'border-border hover:border-primary/40 hover:bg-muted/50'}`
                }>
                
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center text-white text-xs font-800">
                      {sup.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-700 text-foreground truncate">{sup.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-2xs text-muted-foreground mb-1.5">
                    <MapPin size={10} />
                    <span>{sup.zone.replace('_', ' ')}</span>
                  </div>
                  <p className="text-2xs text-success font-700">
                    Crédit dispo: {sup.available_credit.toLocaleString('fr-MA')} MAD
                  </p>
                </button>
              )}
            </div>
          </div>

          {/* Step 2: Product line items */}
          <div className="card-base">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-800">2</div>
                <h3 className="text-sm font-700 text-foreground">Articles de la Commande</h3>
              </div>
              <button
                onClick={() => {if (!selectedSupplierId) {toast.error('Sélectionnez d\'abord un fournisseur');return;}setShowProductPicker(true);}}
                className="btn-primary text-xs py-2 px-3">
                
                <Plus size={13} />
                Ajouter Produit
              </button>
            </div>

            {/* Product picker */}
            {showProductPicker &&
            <div className="mb-4 p-4 bg-muted/50 border border-border rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-700 text-foreground">Catalogue — {selectedSupplier?.name}</p>
                  <button onClick={() => setShowProductPicker(false)} className="text-muted-foreground hover:text-foreground">
                    <X size={14} />
                  </button>
                </div>
                <div className="relative mb-3">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                  type="text"
                  placeholder="Rechercher produit..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="input-base pl-9" />
                
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto scrollbar-thin">
                  {filteredProducts.map((product) => {
                  const effectivePrice = product.has_active_discount ?
                  product.wholesale_price * (1 - product.discount_pct / 100) :
                  product.wholesale_price;
                  return (
                    <button
                      key={product.id}
                      onClick={() => addProduct(product)}
                      className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-card hover:bg-primary/5 border border-border hover:border-primary/30 transition-all text-left">
                      
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                          <AppImage src={product.image} alt={product.alt} width={40} height={40} className="object-cover w-full h-full" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-700 text-foreground truncate">{product.name}</p>
                          <p className="text-2xs text-muted-foreground">{product.unit_type === 'Carton_Box' ? 'Carton' : product.unit_type === 'Kilogram' ? 'kg' : 'Pièce'}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {product.has_active_discount &&
                        <p className="text-2xs text-muted-foreground line-through font-tabular">{product.wholesale_price.toFixed(2)}</p>
                        }
                          <p className={`text-sm font-800 font-tabular ${product.has_active_discount ? 'text-accent' : 'text-foreground'}`}>
                            {effectivePrice.toFixed(2)} MAD
                          </p>
                        </div>
                      </button>);

                })}
                  {filteredProducts.length === 0 &&
                <p className="text-sm text-muted-foreground text-center py-4">
                      {selectedSupplierId ? 'Tous les produits sont déjà ajoutés' : 'Sélectionnez un fournisseur'}
                    </p>
                }
                </div>
              </div>
            }

            {/* Line items table */}
            {lineItems.length > 0 ?
            <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left pb-2 text-xs font-600 text-muted-foreground">Produit</th>
                      <th className="text-right pb-2 text-xs font-600 text-muted-foreground">Prix Unit.</th>
                      <th className="text-center pb-2 text-xs font-600 text-muted-foreground">Quantité</th>
                      <th className="text-right pb-2 text-xs font-600 text-muted-foreground">TVA</th>
                      <th className="text-right pb-2 text-xs font-600 text-muted-foreground">Sous-total HT</th>
                      <th className="text-right pb-2 text-xs font-600 text-muted-foreground">Total TTC</th>
                      <th className="pb-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {lineItems.map((item, index) => {
                    const subtotalHT = item.unit_price * item.quantity;
                    const taxAmount = subtotalHT * item.tax_rate;
                    const totalTTC = subtotalHT + taxAmount;
                    return (
                      <tr key={`line-${item.product_id}`} className="hover:bg-muted/30 transition-colors">
                          <td className="py-3 pr-3">
                            <div className="flex items-center gap-2">
                              <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                                <AppImage src={item.image} alt={item.alt} width={36} height={36} className="object-cover w-full h-full" />
                              </div>
                              <div>
                                <p className="text-xs font-700 text-foreground">{item.product_name}</p>
                                <p className="text-2xs text-muted-foreground">
                                  {item.unit_type === 'Carton_Box' ? 'Carton' : item.unit_type === 'Kilogram' ? 'kg' : 'Pièce'}
                                  {item.has_active_discount &&
                                <span className="ml-1 text-accent font-700">-{item.discount_pct}%</span>
                                }
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 text-right text-sm font-700 text-foreground font-tabular whitespace-nowrap">
                            {item.unit_price.toFixed(2)} MAD
                          </td>
                          <td className="py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <button
                              onClick={() => updateQuantity(index, item.quantity - 1)}
                              className="w-6 h-6 rounded-md bg-muted text-foreground hover:bg-border transition-colors text-sm font-700 flex items-center justify-center">
                              
                                −
                              </button>
                              <input
                              type="number"
                              min={1}
                              value={item.quantity}
                              onChange={(e) => updateQuantity(index, parseInt(e.target.value) || 1)}
                              className="w-12 text-center text-sm font-700 text-foreground bg-muted rounded-md py-1 border border-border focus:outline-none focus:border-primary font-tabular" />
                            
                              <button
                              onClick={() => updateQuantity(index, item.quantity + 1)}
                              className="w-6 h-6 rounded-md bg-muted text-foreground hover:bg-border transition-colors text-sm font-700 flex items-center justify-center">
                              
                                +
                              </button>
                            </div>
                          </td>
                          <td className="py-3 text-right text-xs text-muted-foreground font-tabular">
                            {(item.tax_rate * 100).toFixed(0)}%
                            <br />
                            <span className="text-foreground font-600">{taxAmount.toFixed(2)} MAD</span>
                          </td>
                          <td className="py-3 text-right text-sm font-700 text-foreground font-tabular whitespace-nowrap">
                            {subtotalHT.toFixed(2)} MAD
                          </td>
                          <td className="py-3 text-right text-sm font-800 text-foreground font-tabular whitespace-nowrap">
                            {totalTTC.toFixed(2)} MAD
                          </td>
                          <td className="py-3 pl-2">
                            <button
                            onClick={() => removeItem(index)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors"
                            title="Retirer cet article de la commande">
                            
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>);

                  })}
                  </tbody>
                </table>
              </div> :

            <div className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-border rounded-xl text-center">
                <ShoppingCart size={24} className="text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {selectedSupplierId ? 'Aucun article ajouté — cliquez sur "Ajouter Produit"' : 'Sélectionnez un fournisseur pour commencer'}
                </p>
              </div>
            }
          </div>

          {/* Step 3: Payment method */}
          <div className="card-base">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-800">3</div>
              <h3 className="text-sm font-700 text-foreground">Mode de Paiement</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {paymentMethods.map((pm) => {
                const PMIcon = pm.icon;
                return (
                  <button
                    key={`pm-${pm.value}`}
                    onClick={() => setPaymentMethod(pm.value)}
                    className={`p-3 rounded-xl border text-left transition-all duration-150 ${
                    paymentMethod === pm.value ?
                    'border-primary bg-primary-light ring-2 ring-primary/20' : 'border-border hover:border-primary/40 hover:bg-muted/50'}`
                    }>
                    
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                      paymentMethod === pm.value ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`
                      }>
                        <PMIcon size={14} />
                      </div>
                      <span className="text-xs font-700 text-foreground">{pm.label}</span>
                    </div>
                    <p className="text-2xs text-muted-foreground">{pm.description}</p>
                  </button>);

              })}
            </div>

            {/* Credit warning */}
            {creditWarning &&
            <div className="flex items-start gap-2 mt-3 p-3 bg-danger-light border border-danger/30 rounded-xl">
                <AlertCircle size={14} className="text-danger flex-shrink-0 mt-0.5" />
                <p className="text-xs text-danger font-600">
                  Le montant TTC ({orderTotals.totalTTC.toFixed(2)} MAD) dépasse votre crédit disponible ({selectedSupplier?.available_credit.toLocaleString('fr-MA')} MAD). Choisissez un autre mode de paiement.
                </p>
              </div>
            }
          </div>

          {/* Step 4: Notes */}
          <div className="card-base">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-800">4</div>
              <h3 className="text-sm font-700 text-foreground">Notes de Livraison</h3>
            </div>
            <label className="block text-xs font-600 text-foreground mb-1">Instructions spéciales (optionnel)</label>
            <p className="text-2xs text-muted-foreground mb-1.5">Horaire préféré, accès, température requise, etc.</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="ex: Livraison avant 10h — produits frais uniquement — appeler avant d'arriver"
              className="input-base resize-none" />
            
          </div>
        </div>

        {/* Right — order summary sidebar */}
        <div className="space-y-4">
          <div className="card-base sticky top-[calc(var(--topbar-height)+24px)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-700 text-foreground flex items-center gap-2">
                <Calculator size={15} className="text-primary" />
                Récapitulatif Commande
              </h3>
              <button
                onClick={() => setShowCalc((v) => !v)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border text-xs font-600 transition-all ${showCalc ? 'bg-primary text-white border-primary' : 'bg-muted border-border text-muted-foreground hover:text-foreground hover:border-primary/40'}`}
                title="Ouvrir la calculatrice">
                
                <Calculator size={13} />
                Calc.
              </button>
            </div>

            {/* Calculator widget */}
            {showCalc &&
            <div className="mb-4">
                <OrderCalculator onClose={() => setShowCalc(false)} />
              </div>
            }

            {/* Supplier info */}
            {selectedSupplier &&
            <div className="p-3 bg-muted/50 rounded-xl mb-4">
                <p className="text-2xs text-muted-foreground font-600 mb-0.5">Fournisseur</p>
                <p className="text-sm font-700 text-foreground">{selectedSupplier.name}</p>
                <p className="text-xs text-muted-foreground">{selectedSupplier.zone.replace('_', ' ')}</p>
              </div>
            }

            {/* Totals */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{lineItems.length} article(s)</span>
                <span className="font-600 text-foreground font-tabular">{orderTotals.subtotalHT.toFixed(2)} MAD HT</span>
              </div>

              {/* Tax breakdown */}
              {Object.entries(orderTotals.taxBreakdown).map(([rate, amount]) =>
              amount > 0 &&
              <div key={`tax-breakdown-${rate}`} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">TVA {rate}</span>
                    <span className="font-600 text-muted-foreground font-tabular">+{amount.toFixed(2)} MAD</span>
                  </div>

              )}

              <div className="border-t border-border pt-2 mt-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-700 text-foreground">Total TTC</span>
                  <span className="text-xl font-800 text-foreground font-tabular">{orderTotals.totalTTC.toFixed(2)}</span>
                </div>
                <p className="text-right text-xs text-muted-foreground">MAD</p>
              </div>
            </div>

            {/* Payment method summary */}
            <div className="p-3 bg-primary-light border border-primary/20 rounded-xl mb-4">
              <p className="text-2xs text-muted-foreground font-600 mb-0.5">Mode de Paiement</p>
              <p className="text-sm font-700 text-primary">{paymentMethods.find((p) => p.value === paymentMethod)?.label}</p>
            </div>

            {/* Credit availability for deferred */}
            {paymentMethod === 'Deferred_Credit' && selectedSupplier &&
            <div className={`p-3 rounded-xl border mb-4 ${creditWarning ? 'bg-danger-light border-danger/30' : 'bg-success-light border-success/30'}`}>
                <p className="text-2xs font-600 mb-1 text-muted-foreground">Crédit Disponible</p>
                <p className={`text-sm font-800 font-tabular ${creditWarning ? 'text-danger' : 'text-success'}`}>
                  {selectedSupplier.available_credit.toLocaleString('fr-MA')} MAD
                </p>
                {creditWarning &&
              <p className="text-2xs text-danger mt-1">Dépassement — choisissez un autre mode</p>
              }
              </div>
            }

            {/* Submit button */}
            <button
              onClick={handleSubmit}
              disabled={loading || lineItems.length === 0 || !selectedSupplierId || !!creditWarning}
              className="btn-primary w-full justify-center text-sm py-3">
              
              {loading ?
              <>
                  <Loader2 size={15} className="animate-spin" />
                  Soumission en cours...
                </> :

              <>
                  <ShoppingCart size={15} />
                  Soumettre la Commande
                </>
              }
            </button>

            {lineItems.length === 0 &&
            <p className="text-2xs text-muted-foreground text-center mt-2">Ajoutez des articles pour soumettre</p>
            }
            {!selectedSupplierId &&
            <p className="text-2xs text-muted-foreground text-center mt-2">Sélectionnez un fournisseur pour commencer</p>
            }

            <p className="text-2xs text-muted-foreground text-center mt-3">
              La commande sera soumise en statut <span className="font-700">En Attente</span> et traitée par le fournisseur.
            </p>
          </div>
        </div>
      </div>
    </div>);

}